/**
 * figure.ts — a posable duellist: the body, the blade, and whatever is worn
 * over both.
 *
 * This is the one place that knows how to turn a pose into a scene graph. It
 * exists because there are now two callers — the game view (`renderSync.ts`,
 * driven by snapshots) and the wardrobe preview (`ui/armory.ts`, driven by
 * nothing at all) — and a second implementation of "where does a pauldron go"
 * is a second answer that will drift from the first.
 *
 * ## Anchors
 *
 * The figure exposes MOUNTS (empties it places every frame: head, chest,
 * pelvis, hands, feet, the blade's rolled frame) and BONES (pairs of moving
 * points: upper arms, forearms, thighs, shins). Kit declares which it wants and
 * is posed generically, so adding a piece of armour touches no code here.
 *
 * ## The rule that shapes everything
 *
 * The body is upright and completely independent of the sword. Every earlier
 * version derived the trunk from wherever the pose had put the shoulders, so a
 * stance carrying the blade a long way dragged the shoulders with it and the
 * figure ended up lying on its side. The chest is a few bounded scalars and the
 * shoulders derive FROM them (AGENTS.md: the body is a few bounded scalars).
 * Hands move; a body does not fall over because of it.
 */

import * as THREE from 'three';
import {
  ANKLE_HEIGHT,
  LIMB_UNIT,
  SHIN_BONE,
  STANCE_HALF_WIDTH,
  THIGH_BONE,
  buildBody,
  buildHand,
  buildSword,
} from './art.js';
import { BODY_PARTS, buildKit, type BodyPart, type BoneId, type BuiltKit, type MountId } from './kit.js';
import { createFleshMaterial } from './surfaces.js';
import {
  FEEL,
  TRAJECTORIES,
  emptyLoadout,
  LIMB_IDS,
  LIMB_RADIUS,
  shoulderMidFor,
  skeletonFor,
  weaponPose,
  type AnimStateId,
  type LimbId,
  type Loadout,
} from '@dueler/shared';

/** Everything needed to pose a figure for one frame. */
export interface FigurePose {
  anim: { id: AnimStateId; t: number; angle: number };
  blocking: boolean;
  /** Look pitch, radians. Only the block guard reads it. */
  pitch: number;
  /**
   * The walk cycle, from the snapshot or the predictor.
   *
   * Not measured here any more. The legs are hitboxes, so where they are is
   * simulation state the server owns — a client that invented its own stride
   * would draw shins somewhere a blade would pass straight through.
   */
  gaitPhase: number;
  gaitStride: number;
  /** Optional combo blend from the local predictor. */
  chainFrom?: { hilt: readonly number[]; tip: readonly number[] } | undefined;
  chainBlendMs?: number | undefined;
}

/**
 * Which armour-mounting bone each skeleton limb corresponds to.
 *
 * Only the limbs kit can be strapped to. The trunk, head and feet carry their
 * kit on posed MOUNTS instead, which need no bone.
 */
const LIMB_BONE: Partial<Record<LimbId, BoneId>> = {
  upperArmR: 'upperArmR',
  forearmR: 'forearmR',
  upperArmL: 'upperArmL',
  forearmL: 'forearmL',
  thighR: 'thighR',
  shinR: 'shinR',
  thighL: 'thighL',
  shinL: 'shinL',
};

/** A bone: two points in root space, rewritten every frame. */
type Bone = { from: THREE.Vector3; to: THREE.Vector3 };

export interface Figure {
  /** Carries the player's world position and yaw. Everything hangs off this. */
  root: THREE.Group;
  /**
   * The body group. Hidden in first person, which is why the trunk, legs, head
   * and every piece of kit worn on them live in here while the arms, hands and
   * blade live directly on the root — you keep your own sword, not your own
   * chest.
   */
  body: THREE.Group;
  disposables: { dispose(): void }[];

  // ---- mounts: empties the poser places, kit parents to ----
  head: THREE.Group;
  chest: THREE.Group;
  pelvis: THREE.Group;
  hands: [THREE.Object3D, THREE.Object3D];
  feet: [THREE.Group, THREE.Group];
  /** The blade's rolled frame, so a crossguard added to it stands vertically. */
  weapon: THREE.Group;

  /** The plain pill, hidden when a piece brings its own blade. */
  bareBlade: THREE.Mesh;
  sword: THREE.Object3D;

  // ---- meshes posed directly ----
  torso: THREE.Mesh;
  stomach: THREE.Mesh;
  hips: THREE.Mesh;
  thighs: [THREE.Mesh, THREE.Mesh];
  shins: [THREE.Mesh, THREE.Mesh];
  knees: [THREE.Mesh, THREE.Mesh];
  shoulders: [THREE.Mesh, THREE.Mesh];
  elbows: [THREE.Mesh, THREE.Mesh];
  /** The sword arm: upper arm and forearm. The forearm is also the hurtbox. */
  arm: [THREE.Mesh, THREE.Mesh];
  /** The off arm, both hands on the grip. Cosmetic. */
  offArm: [THREE.Mesh, THREE.Mesh];

  /** Live bone endpoints, rewritten by `poseFigure` and read by fittings. */
  bones: Record<BoneId, Bone>;
  /**
   * The mesh drawn for each limb of the shared skeleton.
   *
   * Keyed by `LimbId`, so adding a limb to the rig and forgetting to draw it is
   * a missing entry here rather than an invisible hitbox. Limbs whose art is a
   * posed GROUP rather than a single capsule — the head and the feet — are
   * absent and handled beside their mounts.
   */
  limbMesh: Partial<Record<LimbId, THREE.Mesh>>;

  /**
   * The hurtboxes, drawn: one wireframe capsule per limb, off by default.
   *
   * These are the shapes the server actually sweeps, reproduced exactly — same
   * radius, same endpoints, and the same `Capsule(length/2, radius)` convention
   * Rapier uses, so the caps stand a radius proud of each joint here just as
   * they do in the physics world.
   *
   * Exact matters more here than anywhere else in the renderer. A debug view
   * that is approximately the hitbox is worse than none: it gets trusted, and
   * then every hit that disagrees with it looks like a physics bug rather than
   * a drawing one. It draws real geometry per limb rather than scaling one
   * capsule, because scaling a capsule squashes its caps and the answer stops
   * being the collider.
   */
  hurtboxes: THREE.Group;
  /**
   * The MOVEMENT capsule, drawn in its own colour.
   *
   * Shown alongside the limbs so the distinction is visible rather than
   * folklore: this one is real, the character controller walks and slides with
   * it, and nothing sweeps against it. It used to be the hurtbox, which is
   * exactly the confusion worth killing on sight.
   */
  movementCapsule: THREE.Mesh;

  /** The bare figure's meshes, grouped by the part a piece of kit can replace. */
  parts: Record<BodyPart, THREE.Object3D[]>;

  /** What is being worn, so a change can be spotted cheaply. */
  loadout: Loadout;
  kit: BuiltKit | undefined;

  /**
   * Stride phase in radians, advanced by ground covered rather than by time,
   * and the smoothed ground speed that sets how big the stride is.
   */
  gaitPhase: number;
  gaitSpeed: number;
  /** Where the figure stood last frame, which is how the two above are measured. */
  lastPos: THREE.Vector3;
}

/** Resting hip height above the feet, m. */
const HIP_HEIGHT = FEEL.BODY_HEIGHT * 0.5;

/** Full stride amplitude is reached at this fraction of top speed. */
const GAIT_FULL_AT = 0.5;

const TWO_PI = Math.PI * 2;

/**
 * Time constant, s, for smoothing the measured ground speed that sets stride
 * length.
 *
 * Remote players are interpolated between 60Hz snapshots, so a per-frame
 * position delta on a 144Hz display arrives in uneven steps and the raw speed
 * from it is far too noisy to drive an amplitude with — the legs would buzz.
 * Short enough that starting and stopping still look immediate.
 */
const GAIT_SMOOTH_S = 0.09;

export function createFigure(isSelf: boolean): Figure {
  const root = new THREE.Group();
  const disposables: { dispose(): void }[] = [];

  const built = buildBody(isSelf);
  const body = built.object as THREE.Group;
  body.name = 'body';
  root.add(body);
  disposables.push(...built.disposables);

  // The blade runs hilt-to-tip over exactly SWORD_LENGTH — the same span the
  // server sweeps — so the picture and the hitbox cannot disagree.
  const builtSword = buildSword();
  root.add(builtSword.object);
  disposables.push(...builtSword.disposables);

  // Arms are two bones each — shoulder to elbow, elbow to hand. One bone ran
  // straight from shoulder to hand and speared through the torso.
  const armGeo = new THREE.CapsuleGeometry(FEEL.ARM_RADIUS, LIMB_UNIT, 6, 12);
  // Skin for both sides. Team colour is a garment now, not a complexion.
  const armMat = createFleshMaterial();
  disposables.push(armGeo, armMat);
  const bone = (): THREE.Mesh => {
    const mesh = new THREE.Mesh(armGeo, armMat);
    mesh.userData['radius'] = FEEL.ARM_RADIUS;
    mesh.castShadow = true;
    mesh.receiveShadow = true;
    root.add(mesh);
    return mesh;
  };
  const arm: [THREE.Mesh, THREE.Mesh] = [bone(), bone()];
  // The off arm is cosmetic — not a hurtbox — but both hands belong on the
  // grip; one floating arm reads as broken.
  const offArm: [THREE.Mesh, THREE.Mesh] = [bone(), bone()];

  // Hands are a MOUNT wrapping the flesh, not the flesh itself. A gauntlet is
  // parented to the mount, so if the two were one object, hiding the hand under
  // a gauntlet would hide the gauntlet too — which is exactly what happened to
  // the great helm before the head was split the same way.
  const rightHand = buildHand(isSelf);
  const leftHand = buildHand(isSelf);
  const handMounts: [THREE.Group, THREE.Group] = [new THREE.Group(), new THREE.Group()];
  handMounts[0].add(rightHand.object);
  handMounts[1].add(leftHand.object);
  root.add(handMounts[0], handMounts[1]);
  disposables.push(...rightHand.disposables, ...leftHand.disposables);

  // ---- the debug hurtboxes ----
  //
  // On the ROOT, not the body: these are the shapes you get hit in, so they
  // must not disappear along with your own torso in first person.
  const hurtboxes = new THREE.Group();
  hurtboxes.visible = false;
  root.add(hurtboxes);

  const hurtboxMat = new THREE.MeshBasicMaterial({
    color: 0x2fe08a,
    wireframe: true,
    transparent: true,
    opacity: 0.6,
    depthWrite: false,
  });
  disposables.push(hurtboxMat);
  for (const id of LIMB_IDS) {
    // Built at a nominal length and REBUILT when the pose changes it, never
    // scaled — see the field comment.
    const geo = new THREE.CapsuleGeometry(LIMB_RADIUS[id], 0.1, 4, 12);
    const mesh = new THREE.Mesh(geo, hurtboxMat);
    mesh.userData['limb'] = id;
    mesh.userData['length'] = 0.1;
    hurtboxes.add(mesh);
    disposables.push(geo);
  }

  // The movement capsule, in its own colour. Not a hurtbox; see the field.
  const moveGeo = new THREE.CapsuleGeometry(
    FEEL.BODY_RADIUS,
    FEEL.BODY_HEIGHT - FEEL.BODY_RADIUS * 2,
    6,
    16,
  );
  const moveMat = new THREE.MeshBasicMaterial({
    color: 0x5b6b86,
    wireframe: true,
    transparent: true,
    opacity: 0.22,
    depthWrite: false,
  });
  const movementCapsule = new THREE.Mesh(moveGeo, moveMat);
  movementCapsule.visible = false;
  root.add(movementCapsule);
  disposables.push(moveGeo, moveMat);

  const emptyBone = (): Bone => ({ from: new THREE.Vector3(), to: new THREE.Vector3() });

  return {
    root,
    body,
    disposables,
    head: built.head,
    chest: built.chest,
    pelvis: built.pelvis,
    hands: handMounts,
    feet: built.feet,
    weapon: builtSword.furniture,
    bareBlade: builtSword.bareBlade,
    sword: builtSword.object,
    torso: built.torso,
    stomach: built.stomach,
    hips: built.hips,
    thighs: built.thighs,
    shins: built.shins,
    knees: built.knees,
    shoulders: built.shoulders,
    elbows: built.elbows,
    arm,
    offArm,
    hurtboxes,
    movementCapsule,
    // Grouped once at build time. Kit hides whole parts rather than fighting
    // individual meshes, so this is the granularity the hiding works at.
    parts: {
      // FLESH only, never a mount — see `BuiltBody.headFlesh`.
      head: built.headFlesh,
      torso: [built.torso, built.stomach, built.hips, built.tabard],
      shoulders: built.shoulders,
      upperArms: [arm[0], offArm[0]],
      elbows: built.elbows,
      forearms: [arm[1], offArm[1]],
      hands: [rightHand.object, leftHand.object],
      thighs: built.thighs,
      knees: built.knees,
      shins: built.shins,
      feet: built.footFlesh,
    },
    bones: {
      upperArmR: emptyBone(),
      forearmR: emptyBone(),
      upperArmL: emptyBone(),
      forearmL: emptyBone(),
      thighR: emptyBone(),
      shinR: emptyBone(),
      thighL: emptyBone(),
      shinL: emptyBone(),
    },
    limbMesh: {
      chest: built.torso,
      stomach: built.stomach,
      pelvis: built.hips,
      upperArmR: arm[0],
      forearmR: arm[1],
      upperArmL: offArm[0],
      forearmL: offArm[1],
      thighR: built.thighs[1],
      shinR: built.shins[1],
      thighL: built.thighs[0],
      shinL: built.shins[0],
    },
    loadout: emptyLoadout(),
    kit: undefined,
    gaitPhase: 0,
    gaitSpeed: 0,
    lastPos: new THREE.Vector3(),
  };
}

/** Where a mount lives on the figure. */
function mountTarget(figure: Figure, at: MountId): THREE.Object3D {
  switch (at) {
    case 'head':
      return figure.head;
    case 'chest':
      return figure.chest;
    case 'pelvis':
      return figure.pelvis;
    case 'handR':
      return figure.hands[0];
    case 'handL':
      return figure.hands[1];
    case 'footR':
      return figure.feet[1];
    case 'footL':
      return figure.feet[0];
    case 'weapon':
      return figure.weapon;
  }
}

/**
 * Whether a bone belongs to the body group.
 *
 * This is what makes first person work without a flag per piece: the arms and
 * hands stay on the root and remain visible, everything on the trunk and legs
 * lives in the body group and vanishes with it. A greave you could still see
 * after your shins had gone would be a strange thing to be wearing.
 */
const isLegBone = (bone: BoneId): boolean => bone.startsWith('thigh') || bone.startsWith('shin');

/**
 * Puts a loadout on, or takes one off.
 *
 * Rebuilds rather than toggling visibility: kit is picked rarely, and holding
 * sixteen pieces of geometry alive for every player in the room to save one
 * rebuild is the wrong trade.
 *
 * Nothing here touches a collider. Kit is drawn over the body capsule and the
 * capsule is what the server hits.
 */
export function wearKit(figure: Figure, loadout: Loadout, isSelf: boolean): void {
  if (figure.kit !== undefined) {
    for (const mount of figure.kit.mounts) mount.object.removeFromParent();
    for (const fitting of figure.kit.fittings) fitting.object.removeFromParent();
    for (const d of figure.kit.disposables) d.dispose();
    figure.kit = undefined;
  }
  figure.loadout = { ...loadout };
  // Everything comes back before anything goes away again, so taking a piece
  // off always restores what it was covering — no accounting, no leaks.
  for (const part of BODY_PARTS) {
    for (const mesh of figure.parts[part]) {
      mesh.visible = true;
      mesh.userData['hiddenByKit'] = false;
    }
  }
  // Taking kit off hands the plain pill back. Restored HERE rather than beside
  // the removal above, because that path also runs on the way into another
  // loadout — and a blade that flickers back to a grey bar for one frame
  // between two swords is worse than either.
  figure.bareBlade.visible = true;

  const kit = buildKit(loadout, isSelf);
  for (const mount of kit.mounts) mountTarget(figure, mount.at).add(mount.object);
  for (const fitting of kit.fittings) {
    (isLegBone(fitting.on) ? figure.body : figure.root).add(fitting.object);
  }
  if (kit.hidesBareBlade) figure.bareBlade.visible = false;
  // Take away what the kit replaces. This is the clipping fix: two curved
  // surfaces a few millimetres apart WILL intersect somewhere across a full
  // range of motion, so an enclosed limb is removed rather than tuned.
  for (const part of kit.hides) {
    for (const mesh of figure.parts[part]) {
      mesh.visible = false;
      // Marked so the per-frame LOD pass cannot re-show a near-detail face part
      // (brow, nose, sockets, scalp) that a closed helm covers.
      mesh.userData['hiddenByKit'] = true;
    }
  }
  // Kit changed which meshes are hidden; force the next LOD pass to recompute.
  // It caches the last distance band to skip a per-frame traverse, so without
  // this a just-covered face would keep showing until the viewer crossed a
  // near/far boundary.
  figure.root.userData['lodNear'] = undefined;
  figure.kit = kit;
}

export function disposeFigure(figure: Figure): void {
  figure.root.removeFromParent();
  for (const d of figure.kit?.disposables ?? []) d.dispose();
  for (const d of figure.disposables) d.dispose();
}

export function setFigureWireframe(figure: Figure, on: boolean): void {
  figure.root.traverse((obj) => {
    if (obj === figure.movementCapsule || obj.parent === figure.hurtboxes) return;
    if (obj instanceof THREE.Mesh && obj.material instanceof THREE.Material) {
      (obj.material as THREE.MeshStandardMaterial).wireframe = on;
    }
  });
}

/** Shows or hides the drawn hurtboxes. Debug only; nothing reads them. */
export function setFigureHitbox(figure: Figure, on: boolean): void {
  figure.hurtboxes.visible = on;
  figure.movementCapsule.visible = on;
}

// ---------------------------------------------------------------------------
// gait
// ---------------------------------------------------------------------------

/**
 * Advances a stride phase by a step of ground travel, in the character's own
 * frame.
 *
 * Phase comes from DISTANCE COVERED, not from a timer, so cadence is correct at
 * every speed for free: one cycle per `GAIT_CYCLE_M` however fast that metre
 * goes past. A timed cycle has to be re-tuned against speed and skates the
 * moment the two disagree.
 *
 * Backwards travel runs the cycle backwards, so a backpedal does not read as a
 * confident stroll in the wrong direction.
 */
export function advanceStridePhase(phase: number, dx: number, dz: number, yaw: number): number {
  const travelled = Math.hypot(dx, dz);
  const forward = dx * -Math.sin(yaw) + dz * -Math.cos(yaw);
  const direction = forward < 0 ? -1 : 1;
  return (phase + (direction * travelled * TWO_PI) / FEEL.GAIT_CYCLE_M + TWO_PI) % TWO_PI;
}

/**
 * Where one foot sits at a phase, in root space: `swing` is fore/aft travel and
 * `lift` is height. `stride` scales the whole thing from 0 (planted, standing)
 * to 1 (full walk).
 *
 * `GAIT_STRIDE` is not a free number. At `GAIT_CYCLE_M / 2π` the foot's world
 * speed at mid-stance is exactly zero — it plants, the body passes over it, and
 * it leaves again — which is what stops the walk looking like a shuffle on ice.
 * A sine cannot hold a foot still through the *whole* contact phase (a real
 * gait has a flat segment there), so some slip either side of mid-stance is
 * inherent to this shape; keeping the amplitude at that value is what makes it
 * least.
 */
export function strideFoot(phase: number, stride: number): { swing: number; lift: number } {
  return {
    // Local forward is -Z, so callers negate: a positive swing is in front.
    swing: Math.sin(phase) * FEEL.GAIT_STRIDE * stride,
    // Lift only through the forward half of the cycle. cos is the derivative of
    // the swing, so it is positive exactly while the foot travels forwards; a
    // foot that rises on the way back is a foot dragged through the floor.
    lift: Math.max(0, Math.cos(phase)) * FEEL.GAIT_LIFT * stride,
  };
}

/**
 * Advances one figure's stride from how far it actually moved this frame.
 *
 * Measured motion, not the snapshot's locomotion clip, and deliberately: an
 * attack owns `anim.id` while it runs, so a clip-driven gait freezes mid-stride
 * every time someone swings — and they can still walk while swinging, at
 * reduced speed. Distance covered is true whatever the animation state says,
 * works identically for the predicted local player and interpolated opponents,
 * and needs nothing added to the wire.
 */
export function advanceFigureGait(figure: Figure, dt: number): void {
  const dx = figure.root.position.x - figure.lastPos.x;
  const dz = figure.root.position.z - figure.lastPos.z;
  figure.lastPos.copy(figure.root.position);

  figure.gaitPhase = advanceStridePhase(figure.gaitPhase, dx, dz, figure.root.rotation.y);

  if (dt > 0) {
    const k = 1 - Math.exp(-dt / GAIT_SMOOTH_S);
    figure.gaitSpeed += (Math.hypot(dx, dz) / dt - figure.gaitSpeed) * k;
  }
}

// ---------------------------------------------------------------------------
// posing
// ---------------------------------------------------------------------------

// Scratch objects: this runs per figure per frame and must not allocate.
const UP = new THREE.Vector3(0, 1, 0);
const AXIS = new THREE.Vector3();
const CHEST_TURN = new THREE.Quaternion();
const TURN_EULER = new THREE.Euler();
const FROM = new THREE.Vector3();
const TO = new THREE.Vector3();
const HIP = new THREE.Vector3();
const ANKLE = new THREE.Vector3();
const KNEE = new THREE.Vector3();
const SHOULDER = new THREE.Vector3();
const RIBS = new THREE.Vector3();
// The foot limbs, kept so the boot mounts can be pitched to match them.
const footL = { from: new THREE.Vector3(), to: new THREE.Vector3() };
const footR = { from: new THREE.Vector3(), to: new THREE.Vector3() };
const DIR = new THREE.Vector3();
const PERP = new THREE.Vector3();
const FORWARD = new THREE.Vector3(0, 0, -1);

/** Points an object built along +Y down a segment, and returns its length. */
function aim(object: THREE.Object3D, from: THREE.Vector3, to: THREE.Vector3): number {
  AXIS.subVectors(to, from);
  const length = AXIS.length();
  if (length >= 1e-6) object.quaternion.setFromUnitVectors(UP, AXIS.divideScalar(length));
  return length;
}

/**
 * Stretches a capsule so it spans exactly `from` to `to`.
 *
 * Scaling a capsule MESH scales its hemispherical caps along with the cylinder,
 * so the drawn height is (cylinder + 2*radius) * scale. The scale that spans a
 * segment is therefore segment / (cylinder + 2*radius) — not
 * (segment - 2*radius) / cylinder, which goes NEGATIVE whenever a segment is
 * shorter than its own diameter and collapsed the torso to nothing.
 */
function span(object: THREE.Object3D, from: THREE.Vector3, to: THREE.Vector3): void {
  object.position.copy(from).add(to).multiplyScalar(0.5);
  const length = aim(object, from, to);
  const radius = (object.userData['radius'] as number | undefined) ?? 0;
  object.scale.set(1, Math.max(0.05, length / (LIMB_UNIT + radius * 2)), 1);
}

/**
 * Solves the knee for a two-bone leg from `hip` down to `ankle`.
 *
 * Unequal bones, so the knee is not the midpoint: it lies where the two circles
 * of radius THIGH_BONE and SHIN_BONE meet, pushed onto the FORWARD side of the
 * hip-to-ankle line. Forward, always — a knee that solved to whichever side
 * scored best would occasionally bend backwards, and there is no pose in this
 * game where that is anything but a broken leg.
 */
function solveKnee(hip: THREE.Vector3, ankle: THREE.Vector3, out: THREE.Vector3): void {
  DIR.subVectors(ankle, hip);
  const distance = DIR.length();
  if (distance < 1e-6) {
    out.copy(hip);
    return;
  }
  DIR.divideScalar(distance);

  // Clamped so the two circles always intersect, whatever the gait asks for.
  const reach = Math.min(
    Math.max(distance, Math.abs(THIGH_BONE - SHIN_BONE) + 1e-3),
    THIGH_BONE + SHIN_BONE - 1e-3,
  );
  const along = (reach * reach + THIGH_BONE * THIGH_BONE - SHIN_BONE * SHIN_BONE) / (2 * reach);
  const offset = Math.sqrt(Math.max(0, THIGH_BONE * THIGH_BONE - along * along));

  // The component of forward that is perpendicular to the leg axis.
  PERP.copy(FORWARD).addScaledVector(DIR, -FORWARD.dot(DIR));
  if (PERP.lengthSq() < 1e-9) PERP.set(0, 0, -1);
  PERP.normalize();

  out.copy(hip).addScaledVector(DIR, along).addScaledVector(PERP, offset);
}

/**
 * Places one debug hurtbox exactly on its limb.
 *
 * The geometry is REBUILT when the limb's length changes rather than scaled,
 * because scaling a capsule squashes its hemispherical caps — and then the
 * thing being drawn is no longer the capsule the server tests, which is the
 * only reason to draw it. Rebuilds are rare: most limbs are a fixed bone
 * length and only the arms, solved by IK onto a moving grip, ever vary.
 */
function poseHurtbox(figure: Figure, id: LimbId, from: THREE.Vector3, to: THREE.Vector3): void {
  const mesh = figure.hurtboxes.children.find((child) => child.userData['limb'] === id);
  if (!(mesh instanceof THREE.Mesh)) return;

  const length = from.distanceTo(to);
  // `CapsuleGeometry(radius, length)` gives a CYLINDER of `length` with caps
  // beyond it — exactly Rapier's `Capsule(length / 2, radius)`.
  if (Math.abs((mesh.userData['length'] as number) - length) > 5e-3) {
    mesh.geometry.dispose();
    mesh.geometry = new THREE.CapsuleGeometry(LIMB_RADIUS[id], length, 4, 12);
    mesh.userData['length'] = length;
  }
  mesh.position.copy(from).add(to).multiplyScalar(0.5);
  aim(mesh, from, to);
}

/**
 * Poses the whole figure for one frame.
 *
 * The blade and both arms come from `weaponPose` — the same function, and the
 * same baked samples, that the server sweeps with. Poses are in root space,
 * measured from the FEET, while the group sits at the capsule centre, hence the
 * half-height offset applied throughout.
 */
export function poseFigure(figure: Figure, snap: FigurePose): void {
  const pose = weaponPose({
    animId: snap.anim.id,
    t: snap.anim.t,
    angle: snap.anim.angle,
    blocking: snap.blocking,
    pitch: snap.pitch,
    dial: snap.anim.angle,
    trajectories: TRAJECTORIES,
    chainFrom: snap.chainFrom,
    chainBlendMs: snap.chainBlendMs,
  });

  const offset = FEEL.BODY_HEIGHT / 2;
  /** Reads a root-space pose point into a scratch vector, in group space. */
  const at = (v: THREE.Vector3, p: readonly number[]): THREE.Vector3 =>
    v.set(p[0]!, p[1]! - offset, p[2]!);

  const bones = figure.bones;

  // The shoulder line, taken from the chest attitude directly rather than from
  // the top of the chest limb. The two used to be the same point; the trunk now
  // carries on ABOVE the joint, and reading the mount off it would lift every
  // piece of chest armour with it.
  at(SHOULDER, shoulderMidFor(pose.torso));

  // ---- the blade ----
  // Modelled from its hilt, so it is placed there rather than at a midpoint.
  at(FROM, pose.hilt);
  at(TO, pose.tip);
  figure.sword.position.copy(FROM);
  aim(figure.sword, FROM, TO);

  // ---- the body ----
  //
  // Every limb comes from `skeletonFor`, which is the SAME call the server
  // poses its hurtbox capsules with. That is the whole architecture in one
  // line: there is no separate idea of where a shin is drawn and where a shin
  // can be cut (AGENTS.md: one transform sequence).
  const limbs = skeletonFor({
    pose,
    gaitPhase: snap.gaitPhase,
    stride: snap.gaitStride,
  });

  for (const limb of limbs) {
    at(FROM, limb.from);
    at(TO, limb.to);
    const bone = LIMB_BONE[limb.id];
    if (bone !== undefined) {
      bones[bone].from.copy(FROM);
      bones[bone].to.copy(TO);
    }
    const mesh = figure.limbMesh[limb.id];
    if (mesh !== undefined) span(mesh, FROM, TO);
    if (figure.hurtboxes.visible) poseHurtbox(figure, limb.id, FROM, TO);
    // A few limbs are also landmarks the mounts hang off.
    if (limb.id === 'stomach') HIP.copy(FROM);
    if (limb.id === 'footL') {
      footL.from.copy(FROM);
      footL.to.copy(TO);
    }
    if (limb.id === 'footR') {
      footR.from.copy(FROM);
      footR.to.copy(TO);
    }
  }

  // The chest is drawn from the SAME twist the shoulders were placed with, so
  // the trunk turns with them rather than the arms rotating off a static body.
  // Its section is oval, so the turn is visible.
  //
  // NEGATIVE pitch tips the figure forward. It faces -Z, so a positive rotation
  // about +X lifts that facing towards the sky — which had the chest leaning
  // back into a thrust, and made the bow look up at the ceiling.
  CHEST_TURN.setFromEuler(TURN_EULER.set(-pose.torso.lean, pose.torso.twist, 0, 'YXZ'));
  figure.torso.quaternion.premultiply(CHEST_TURN);

  // The chest MOUNT: origin on the shoulder line, turned with the chest and
  // folded with it. Kit authored in this space gets the twist, the lean, the
  // lunge and the bow for nothing, and — because the shoulders are derived from
  // the same transform — a pauldron hung at ±SHOULDER_HALF_WIDTH lands exactly
  // on the joint the arm is solved from.
  figure.chest.position.copy(SHOULDER);
  figure.chest.quaternion.setFromEuler(
    TURN_EULER.set(-pose.torso.lean - pose.torso.fold, pose.torso.twist, 0, 'YXZ'),
  );
  // The pelvis does not twist: the shoulders come round into a cut and the hips
  // stay where the feet put them. A fauld that turned with the chest would
  // scissor across the thighs underneath it.
  figure.pelvis.position.copy(HIP);

  // Joints and feet sit on the skeleton's own endpoints.
  figure.knees[0]!.position.copy(bones.thighL.to);
  figure.knees[1]!.position.copy(bones.thighR.to);

  // The foot mount is placed at the ankle and PITCHED to match the foot limb,
  // rather than aimed down it. A boot is authored around an ankle with its toes
  // at -Z; aiming +Y along a bone would roll the whole shoe onto its side as
  // the leg swings, and at full stride it went through the floor.
  for (const [foot, ankle, toe] of [
    [figure.feet[0], footL.from, footL.to],
    [figure.feet[1], footR.from, footR.to],
  ] as const) {
    foot.position.copy(ankle);
    const drop = ankle.y - toe.y;
    const along = Math.hypot(toe.x - ankle.x, toe.z - ankle.z);
    // Negative pitches the toe DOWN, which is what a lifted foot does.
    foot.rotation.set(-Math.atan2(drop, Math.max(1e-4, along)), 0, 0);
  }

  // ---- the head ----
  figure.head.position.copy(SHOULDER);
  // The head rides the chest, but only takes part of the twist: a duellist
  // keeps their eyes on their opponent while their shoulders come round.
  //
  // A FOLD is the exception and is taken almost in full. The body bends by its
  // trunk endpoints MOVING, which rotates nothing — so without this the head
  // rides a bowed body still looking dead level. Just over 1 so the head leads
  // slightly; much over and he pitches into the floor.
  figure.head.quaternion.setFromEuler(
    TURN_EULER.set(
      -(pose.torso.lean * 0.4 + pose.torso.fold * 1.15),
      pose.torso.twist * 0.45,
      0,
      'YXZ',
    ),
  );

  // ---- joints and hands ----
  figure.shoulders[0].position.copy(bones.upperArmR.from);
  figure.shoulders[1].position.copy(bones.upperArmL.from);
  figure.elbows[0].position.copy(bones.forearmR.from);
  figure.elbows[1].position.copy(bones.forearmL.from);
  // Hands are ORIENTED, not just placed: +Y runs back up the forearm towards
  // the elbow. Without this a hand is a point with no frame, so a gauntlet
  // built to run up the arm came out aimed at the ceiling instead — the cuff
  // stuck out sideways like a dinner plate whatever the arm was doing.
  for (const [hand, bone] of [
    [figure.hands[0], bones.forearmR],
    [figure.hands[1], bones.forearmL],
  ] as const) {
    hand.position.copy(bone.to);
    aim(hand, bone.to, bone.from);
  }

  // ---- kit that rides a bone ----
  //
  // One generic loop, which is the point of the whole anchor scheme: every
  // piece mounted on an empty was posed by the blocks above without knowing it
  // existed, and everything left is spanned or placed here by the same three
  // lines whether it is a mail sleeve or a knee cop.
  for (const fitting of figure.kit?.fittings ?? []) {
    const bone = bones[fitting.on];
    if (fitting.fit === 'span') {
      span(fitting.object, bone.from, bone.to);
      continue;
    }
    fitting.object.position.lerpVectors(bone.from, bone.to, fitting.t ?? 0);
    aim(fitting.object, bone.from, bone.to);
  }
}
